import { CustomersController } from '@/shared/endpoints/customers';
import { resetDashboardState } from '@/store/slices/dashboard';
import { resetOnboardingState } from '@/store/slices/onboarding';
import { resetPersistSlice } from '../../store/helpers';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { store } from '../../store';
import { GtmEvent } from '../types/enums';
import { userDetails } from '../../store/slices/onboarding/selectors';
import { selectIsAuthorized } from '../../store/slices/auth/selectors';
import { useAppSelector } from './store';
import { useActivityMonitor } from './useActivityMonitor';
import { clearLastActivity } from '@/shared/helpers/lastActivity';

const INACTIVITY_TIMEOUT_MS = Number(import.meta.env.VITE_APP_LOGOUT_TIMEOUT_MS)

type ResetOptions = {
    logout: boolean;
};

export const logoutWithStoreReset = async (options: ResetOptions) => {
    try {
        if (options.logout) {
            await store.dispatch(CustomersController.logout());
        }
    } catch (error) {
        console.error('Logout failed:', error);
    } finally {
        window.sessionStorage.removeItem('pID'); // post hog id collected from incoming redirect decoded from code
        window.sessionStorage.removeItem('utm_source');
        window.sessionStorage.removeItem('utm_medium');
        window.sessionStorage.removeItem('utm_campaign');
        window.sessionStorage.removeItem('utm_content');
        window.sessionStorage.removeItem('utm_term');
        window.sessionStorage.removeItem('fbclid');
        window.sessionStorage.removeItem('gclid');
        window.sessionStorage.removeItem('sessionRecordingPromptShown'); // reset session recording prompt for next login
        clearLastActivity();
        resetPersistSlice('auth');
        store.dispatch(resetDashboardState());
        store.dispatch(resetOnboardingState());
    }
};

export const useLogoutWithStoreReset = () => {
    const user = useAppSelector(userDetails);
    const sendDataToGTM = useGTMDispatch();

    return async () => {
        const userId = user?.id;
        sendDataToGTM({ event: GtmEvent.LOGOUT, userId });
        await logoutWithStoreReset({ logout: true });
    };
};

// logs the user out once 15 minutes pass with no click/keydown/scroll/mousemove activity
// since the last-activity timestamp (seeded in sessionStorage on login, see store/slices/auth);
// only monitors while actually authenticated
export const useLogoutOnInactivity = () => {
    const isAuthorized = useAppSelector(selectIsAuthorized);
    const logout = useLogoutWithStoreReset();

    useActivityMonitor(() => {
        logout();
    }, INACTIVITY_TIMEOUT_MS, isAuthorized);
};

export default useLogoutWithStoreReset;

